'use client';

import React, { useState } from 'react';
import Button from '@/components/ui/Button';

const PetFoodCalculator = () => {
    const [petType, setPetType] = useState<'dog' | 'cat'>('dog');
    const [weight, setWeight] = useState<string>('');
    const [activity, setActivity] = useState<'low' | 'moderate' | 'high'>('moderate');
    const [result, setResult] = useState<number | null>(null);

    const calculateCalories = () => {
        const w = parseFloat(weight);
        if (!w || isNaN(w)) return;

        let rer = 70 * Math.pow(w / 2.20462, 0.75); // Resting Energy Requirement (weight in kg)
        let factor = 1.0;

        if (petType === 'dog') {
            switch (activity) {
                case 'low': factor = 1.6; break; // Neutered/Adult
                case 'moderate': factor = 1.8; break; // Intact/Active
                case 'high': factor = 2.5; break; // Working/Puppy
            }
        } else {
            switch (activity) {
                case 'low': factor = 1.2; break; // Neuteured/Inactiv
                case 'moderate': factor = 1.4; break; // Intact/Active
                case 'high': factor = 1.6; break; // Kitten
            }
        }

        setResult(Math.round(rer * factor));
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
                <button
                    onClick={() => setPetType('dog')}
                    className={`p-4 rounded-xl border-2 transition-all ${petType === 'dog'
                            ? 'border-brand-primary bg-brand-primary/5'
                            : 'border-border hover:border-brand-primary/50'
                        }`}
                >
                    <span className="text-2xl block mb-2">🐕</span>
                    <span className="font-semibold">Dog</span>
                </button>
                <button
                    onClick={() => setPetType('cat')}
                    className={`p-4 rounded-xl border-2 transition-all ${petType === 'cat'
                            ? 'border-brand-primary bg-brand-primary/5'
                            : 'border-border hover:border-brand-primary/50'
                        }`}
                >
                    <span className="text-2xl block mb-2">🐈</span>
                    <span className="font-semibold">Cat</span>
                </button>
            </div>

            <div>
                <label className="block text-sm font-medium mb-2">Weight (lbs)</label>
                <input
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-input bg-background focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none transition-all"
                    placeholder="e.g., 25"
                />
            </div>

            <div>
                <label className="block text-sm font-medium mb-2">Activity Level</label>
                <div className="space-y-2">
                    {[
                        { id: 'low', label: 'Low (Senior / Couch Potato)' },
                        { id: 'moderate', label: 'Moderate (Daily Walks / Play)' },
                        { id: 'high', label: 'High (Working / Puppy / kitten)' }
                    ].map((opt) => (
                        <label key={opt.id} className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:bg-muted cursor-pointer">
                            <input
                                type="radio"
                                name="activity"
                                checked={activity === opt.id}
                                onChange={() => setActivity(opt.id as any)}
                                className="text-brand-primary focus:ring-brand-primary"
                            />
                            <span>{opt.label}</span>
                        </label>
                    ))}
                </div>
            </div>

            <Button onClick={calculateCalories} className="w-full" size="lg">
                Calculate Daily Needs
            </Button>

            {result && (
                <div className="mt-6 p-4 bg-accent/10 rounded-xl border border-accent/20 text-center">
                    <p className="text-sm text-muted-foreground mb-1">Estimated Daily Calories</p>
                    <p className="text-4xl font-bold text-brand-primary">{result} <span className="text-lg text-muted-foreground">kcal</span></p>
                    <p className="text-xs text-muted-foreground mt-2">
                        *This is an estimate. Consult your vet for specific advice.
                    </p>
                </div>
            )}
        </div>
    );
};

export default PetFoodCalculator;
